import { useCollectionsStore } from '../../store/collectionsStore';
import { redactSecretValues } from '../../utils/aiPreRequestDryRun';
import { buildRequestUrl } from '../../utils/urlParams';
import { resolveDynamicVariable } from '../../utils/dynamicVariables';
import { createVariableResolver } from '../../utils/resolveVariables';
import { folderVariablesForTab } from '../../utils/folderVariables';

/** Max response-body characters returned to the model (keeps token usage bounded). */
export const AI_RESPONSE_BODY_LIMIT = 20000;

/** Character cap for a before/after value in an `update_request` result. */
export const REQUEST_DIFF_VALUE_MAX = 120;

/**
 * One request field as a short string, for the `update_request` before/after
 * diff the chat card renders.
 *
 * Headers collapse to their KEY NAMES on purpose. A header value is where an
 * Authorization token lives, this string lands in the chat transcript, and
 * this function is the last point that sees the real values — so the reduction
 * belongs here rather than in the view. (`looksSecret` is not enough on its
 * own: `Authorization: Bearer <jwt>` matches neither its name pattern nor its
 * value patterns, because the `Bearer ` prefix breaks the JWT anchor.)
 *
 * Everything else is clipped so a large body cannot bloat message state.
 */
export function describeRequestFieldValue(field, value) {
  if (value === undefined || value === null) return '';
  if (field === 'headers') {
    return (Array.isArray(value) ? value : [])
      .map((h) => (h && h.key ? String(h.key).trim() : ''))
      .filter(Boolean)
      .join(', ');
  }
  const text = String(value).replace(/\s+/g, ' ').trim();
  return text.length > REQUEST_DIFF_VALUE_MAX
    ? `${text.slice(0, REQUEST_DIFF_VALUE_MAX - 1)}…`
    : text;
}

/**
 * The four variable scopes in the shape `scriptVariables.js` expects, so an AI
 * script run resolves `{{vars}}` exactly as the send path does — including the
 * `secret` flag.
 *
 * This replaced a local `envSnapshotFromStore` helper that flattened the same
 * four scopes while ignoring `secret` entirely. `aiGeneratePreRequestScript`
 * was moved off it when the dry-run tool was built; `aiGenerateTests` was not,
 * and that was the whole of the leak fixed on this branch. There is now one
 * way to build a script's variables on the AI path, and it is this one — do not
 * add a second.
 */
export function scriptScopesFromStore(vars, collectionId, folderVariables = null) {
  return {
    globalVariables: vars.globalVariables,
    workspaceVariables: vars.workspaceVariables,
    environmentVariables: vars.selectedEnvironment?.variables,
    collectionVariables: collectionId ? vars.collectionVariablesById?.[collectionId] : null,
    // The tab's folder chain (nearest first) — a secret folder var is a secret
    // like any other on the AI path (COL-02).
    folderVariables,
  };
}

/** Folder variables for a tab, from the sidebar collections (utils/folderVariables). */
export function tabFolderVariables(tab) {
  return folderVariablesForTab(tab, useCollectionsStore.getState().collections);
}

export function responseBodyString(response) {
  const data = response?.data;
  if (data == null) return '';
  return typeof data === 'string' ? data : JSON.stringify(data);
}

/**
 * What a `generate_tests` run may say about itself, once secrets are scrubbed.
 *
 * Every string here is script-chosen. `pm.test(name, fn)` takes the label from
 * the script, and `error` is whatever the assertion threw — chai's own message
 * embeds the ACTUAL value, so an ordinary failing assertion
 * (`expect(pm.environment.get('K')).to.equal('WRONG')`) prints the real value
 * with no adversarial intent at all. Both go to the model verbatim through
 * `condenseToolResultForApi`, so both are scrubbed here, and the array is
 * rebuilt field by field rather than spread: a future field on a sandbox test
 * result must be opted in, not opted out.
 *
 * ## The limit, stated plainly
 *
 * This is a substring filter and nothing more. `pm.environment.get('K')
 * .split('').join('.')` produces no literal occurrence of the value and walks
 * straight through it, as does `.slice(0, 12)`. It is a backstop for the
 * non-hostile case; it is NOT containment, and no comment here should say it
 * is.
 *
 * Containment for a NO-GRANT secret comes from the caller: the value is never
 * in the sandbox (`buildScriptVariableMap` + `withheldKeys`), so there is
 * nothing for a script to transform. For a `scriptAccess: true` secret the
 * value genuinely is in the sandbox — that is what the grant means, and
 * `generate_tests` needs it (see the caller) — so a hostile script CAN encode
 * it out. That is a residual, accepted risk, identical in kind to the one the
 * Send path already carries, and it requires the user to have granted that one
 * variable to scripts.
 */
export function redactTestRunResults(tests, secretValues = []) {
  const scrub = (text) => redactSecretValues(String(text ?? ''), secretValues);
  return (tests || []).map((t) => ({
    name: scrub(t?.name),
    passed: t?.passed === true,
    error: t?.error == null || t.error === '' ? null : scrub(t.error),
  }));
}

/**
 * The URL a pre-request script sees during an AI dry run.
 *
 * Mirrors RequestBuilder's `buildUrl()`: substitute `{{vars}}`, then fold in
 * the enabled Params-tab rows. Before this the dry run passed `tab.url` raw,
 * so any script doing `new URL(pm.request.url)` — the ordinary way to read a
 * host or a path — failed the dry run on a script that works perfectly at Send,
 * and the model would "repair" working code into something wrong.
 *
 * `map` is the dry run's variable map, which has every secret removed. A
 * `{{SIGNING_KEY}}` in the URL therefore stays literal rather than resolving,
 * which is a deliberate divergence from Send: pm.request.url is readable from
 * inside the sandbox, so substituting a secret into it would put the value
 * back in the very place the withholding takes it out of. Unknown names are
 * left as-is by both paths anyway, so the script still parses.
 */
export function substituteForDryRun(text, map) {
  if (!text || typeof text !== 'string') return text || '';
  const resolve = createVariableResolver(
    (name) => (Object.prototype.hasOwnProperty.call(map, name) ? map[name] : null),
    { fallback: resolveDynamicVariable }
  );
  return resolve(text);
}

export function dryRunRequestUrl(tab, map) {
  const substituted = substituteForDryRun(tab?.url ?? '', map);
  const enabledParams = (tab?.params || [])
    .filter((p) => p?.key && p.enabled)
    .map((p) => ({
      key: substituteForDryRun(p.key, map),
      value: substituteForDryRun(p.value ?? '', map),
      enabled: true,
    }));
  try {
    return buildRequestUrl(substituted, enabledParams);
  } catch {
    return substituted;
  }
}

export const headersFromKvRows = (rows) => {
  const headers = {};
  (rows || []).forEach((h) => {
    if (h?.key && h.enabled !== false) headers[h.key] = h.value || '';
  });
  return headers;
};
